import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  onSnapshot,
  QueryConstraint,
  Timestamp,
  serverTimestamp,
  addDoc,
  where,
  limit
} from 'firebase/firestore';
import { db } from './config';
import { ActionType, EntityType } from '../../types/deal.types';

export const createDocument = async <T extends { id?: string }>(
  collectionName: string,
  data: T
): Promise<string> => {
  try {
    const docRef = data.id 
      ? doc(db, collectionName, data.id)
      : doc(collection(db, collectionName));
    
    await setDoc(docRef, {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return docRef.id;
  } catch (error: unknown) {
    throw new Error(`Failed to create document: ${(error instanceof Error ? error.message : String(error))}`);
  }
};

export const getDocument = async <T>(
  collectionName: string,
  documentId: string
): Promise<T | null> => {
  try {
    const docRef = doc(db, collectionName, documentId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as T;
    }
    
    return null;
  } catch (error: unknown) {
    throw new Error(`Failed to get document: ${(error instanceof Error ? error.message : String(error))}`);
  }
};

export const updateDocument = async <T>(
  collectionName: string,
  documentId: string,
  data: Partial<T>
): Promise<void> => {
  try {
    const docRef = doc(db, collectionName, documentId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
  } catch (error: unknown) {
    throw new Error(`Failed to update document: ${(error instanceof Error ? error.message : String(error))}`);
  }
};

export const deleteDocument = async (
  collectionName: string,
  documentId: string
): Promise<void> => {
  try {
    await deleteDoc(doc(db, collectionName, documentId));
  } catch (error: unknown) {
    throw new Error(`Failed to delete document: ${(error instanceof Error ? error.message : String(error))}`);
  }
};

export const getCollection = async <T>(
  collectionName: string,
  constraints: QueryConstraint[] = []
): Promise<T[]> => {
  try {
    const q = query(collection(db, collectionName), ...constraints);
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as T));
  } catch (error: unknown) {
    throw new Error(`Failed to get collection: ${(error instanceof Error ? error.message : String(error))}`);
  }
};

export const subscribeToDocument = <T>(
  collectionName: string,
  documentId: string,
  callback: (data: T | null) => void
) => {
  const docRef = doc(db, collectionName, documentId);
  
  return onSnapshot(docRef, (doc) => {
    if (doc.exists()) {
      callback({ id: doc.id, ...doc.data() } as T);
    } else {
      callback(null);
    }
  });
};

export const subscribeToCollection = <T>(
  collectionName: string,
  constraints: QueryConstraint[] = [],
  callback: (data: T[]) => void,
  errorCallback?: (error: Error) => void
) => {
  const q = query(collection(db, collectionName), ...constraints);
  
  return onSnapshot(
    q, 
    (querySnapshot) => {
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as T));
      callback(data);
    },
    (error) => {
      console.error(`Error subscribing to ${collectionName}:`, error);
      if (errorCallback) {
        errorCallback(error);
      }
    }
  );
};

export const logActivity = async (
  userId: string,
  userEmail: string,
  action: ActionType,
  entity: EntityType,
  entityId: string,
  entityName: string,
  changes?: any[],
  status: 'success' | 'failed' = 'success',
  errorMessage?: string
): Promise<void> => {
  try {
    const activityLogData: any = {
      userId,
      userEmail,
      action,
      entity,
      entityId,
      entityName,
      changes: changes || [], // Ensure changes is never undefined
      ipAddress: 'N/A', // In production, get from request
      userAgent: navigator.userAgent,
      timestamp: Timestamp.now(),
      status
    };
    
    // Only add errorMessage if it exists
    if (errorMessage) {
      activityLogData.errorMessage = errorMessage;
    }
    
    await addDoc(collection(db, 'activityLogs'), activityLogData);
  } catch (error) {
    console.error('Failed to log activity:', error);
  }
};

// Get user document by email - useful during onboarding when Auth UID doesn't match doc ID
export const getUserByEmail = async (email: string): Promise<any | null> => {
  try {
    const usersQuery = query(
      collection(db, 'users'),
      where('email', '==', email),
      limit(1)
    );
    const querySnapshot = await getDocs(usersQuery);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user by email:', error);
    throw error;
  }
};